import {Component, OnInit, TemplateRef, ViewChild, ViewChildren, ViewContainerRef} from '@angular/core';
import {KnockoutFieldComponent} from "../../../../core/components/knockout-field/knockout-field.component";
import {Subject, Subscription} from "rxjs";
import {KnockoutFieldModel} from "../../../../core/components/knockout-field/knockout-field.model";
import {Location} from "@angular/common";
import {ActivatedRoute, Router} from "@angular/router";
import {RequestService} from "../../../../core/services/request/request.service";
import {LegacyNavigateService} from "../../../../core/services/legacy-navigate/legacy-navigate.service";
import {ProgressDialogComponent} from "../../../../core/components/progress-dialog/progress-dialog.component";
import {InternationalizationService} from "../../../../core/services/internationalization/internationalization.service";

enum LoadRecordsMode {
  LoadRecords,
  LoadRecordsSuccess,
}

@Component({
  selector: 'sb-load-records',
  templateUrl: './load-records.component.html',
  styleUrls: ['./load-records.component.scss']
})
export class LoadRecordsComponent implements OnInit {

  // Progress dialog

  progressTitle = "Loading records";
  progressTopic = "import_controller.submit";
  progressAutoClose = true;
  @ViewChild("progressDialog") progressDialog: ProgressDialogComponent;

  // Other support
  @ViewChildren(KnockoutFieldComponent)
  knockoutFields: KnockoutFieldComponent[];

  LoadRecordsMode = LoadRecordsMode; // Needed to access enum in the template

  page: LoadRecordsMode = LoadRecordsMode.LoadRecords;

  validationFailed: boolean = false;

  importImportTransform;
  importImportTable;
  importImportLoadType;

  responseData;

  // Set up a mechanism for us to communicate with the child components (e.g. to ask for validation)
  eventsSubject: Subject<any> = new Subject<any>();

  importLoadType: KnockoutFieldModel = {
    name: "ImportLoadType",
    i18nKey: "Import load type",
    datatype: 'choice',
    availableValues: [ // Why does this need displayValue: AND i18nKey: ?
      {value: 'create', displayValue: 'Create', i18nKey: 'Create'},
      {value: 'update', displayValue: 'Update', i18nKey: 'Update'},
      {value: 'delete', displayValue: 'Delete', i18nKey: 'Delete'},
      {value: 'sync', displayValue: 'Sync', i18nKey: 'Sync'},
    ],
    referenceTable: null,
    readonly: false,
    mandatory: true,
    enablePreview: false,
    tooltip: null,
    data: {
      defaultValue: {value: 'sync', displayValue: 'Sync', i18nKey: 'Sync'},
      defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
      answer: null, // null | {value?: string, displayValue?: string };
    },
    requestId: null,
    table: 'SyntheticTable',
    renderer: "choice",
    rendererProperties: {},
    valueChangeCallback: (value) => {
      if (value != undefined) {
        this.importImportLoadType = value;
      }
    },
  };

  importTransformTableList: KnockoutFieldModel = {
    name: "ImportTransform",
    i18nKey: "Import transform",
    datatype: 'reference',
    availableValues: [],
    referenceTable: "ImportTransform",
    readonly: false,
    mandatory: true,
    enablePreview: false,
    tooltip: null,
    data: {
      defaultValue: null, // null | {value?: string, displayValue?: string };
      defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
      answer: {
        value: null,
        displayValue: null
      }, // null | {value?: string, displayValue?: string };
    },
    requestId: null,
    table: 'SyntheticTable',
    renderer: "onetoone",
    rendererProperties: {},
    valueChangeCallback: (value) => {
      if (value != undefined) {
        this.importImportTransform = value;
      }
    },
  };

  importTableTableList: KnockoutFieldModel = {
    name: "ImportTable",
    i18nKey: "Import staging table",
    datatype: 'reference',
    availableValues: [],
    referenceTable: "ImportTable",
    readonly: false,
    mandatory: false,
    enablePreview: false,
    tooltip: null,
    data: {
      defaultValue: null, // null | {value?: string, displayValue?: string };
      defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
      answer: {
        value: null,
        displayValue: null
      }, // null | {value?: string, displayValue?: string };
    },
    requestId: null,
    table: 'SyntheticTable',
    renderer: "onetoone",
    rendererProperties: {},
    valueChangeCallback: (value) => {
      if (value != undefined) {
        this.importImportTable = value;
      }
    },
  };

  constructor(private location: Location,
              private router: Router,
              private activatedRoute: ActivatedRoute,
              private requestService: RequestService,
              private legacyNavigateService: LegacyNavigateService,
              private i18nService: InternationalizationService) { }

  ngOnInit(): void {
    // Wipe out the page context
    (window as any).KO_PAGE_CONTEXT.clear();

    // Wipe out the Knockout content.
    const pageContentMain = document.getElementById("page-content-main");
    if (pageContentMain) {
      pageContentMain.innerHTML = "";
    }

    // Mark as import page
    document.getElementById("detailMain")?.classList?.add("sb-import-page-content");
  }

  ngOnDestroy() {
    document.getElementById("detailMain")?.classList?.remove("sb-import-page-content");
  }

  async submitLoadRecords() {

    this.validationFailed = false;

    await this.eventsSubject.next("validate");

    if (!this.importImportTransform || ! this.importImportLoadType) {
      this.validationFailed = true;
    }

    if (!this.validationFailed) {

      this.progressDialog.showDialog();

      const data = {
        importLoadType: this.importImportLoadType,
        importTransform: this.importImportTransform,
        importTable: this.importImportTable,
      };

      try {
        const response: any = await this.requestService.createRequest()
          .controller("servicely.core.Import")
          .method("loadRecords")
          .data(data)
          .execute();

        this.responseData = response.data;

        if (this.responseData && this.responseData.success) {
          this.page = LoadRecordsMode.LoadRecordsSuccess;
        }
      } catch (e) {
        this.responseData = { success: false, error: e };
      } finally {
        this.progressDialog.hideDialog();
      }
    }
  }

  buttonLabel(key: string): string {
    return this.i18nService.getMessage(key);
  }

  stringify(obj: any) {
    return JSON.stringify(obj, null, 2);
  }
}
